import { useMemo, useState } from "react";
import PageHero from "@/components/feature/PageHero";
import CtaBanner from "@/components/feature/CtaBanner";
import SectionHeading from "@/components/base/SectionHeading";
import { galleryItems, galleryCategories } from "@/mocks/gallery";
import { HERO_IMAGES } from "@/config/heroImages";

export default function GalleryPage() {
  const [active, setActive] = useState("All");
  const [lightbox, setLightbox] = useState<number | null>(null);

  const visibleItems = useMemo(
    () => (active === "All" ? galleryItems : galleryItems.filter((item) => item.category === active)),
    [active],
  );

  const closeLightbox = () => setLightbox(null);
  const step = (direction: number) => {
    setLightbox((current) => {
      if (current === null) return current;
      const next = (current + direction + visibleItems.length) % visibleItems.length;
      return next;
    });
  };

  return (
    <>
      <PageHero
        eyebrow="Gallery"
        title="Image Gallery"
        subtitle="A glimpse of our packing crews, trucks, warehouse and completed moves across Bangalore, Mysore and Tumkur."
        crumbs={[{ label: "Home", href: "/" }, { label: "Gallery" }]}
        image={HERO_IMAGES.gallery}
      />

      <section className="bg-background-50 py-14 md:py-16">
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6">
          <SectionHeading
            eyebrow="Our Work"
            title="Moments from recent moves"
            subtitle="Filter by the type of move to see how we handle households, offices and stored goods."
          />

          {/* Filter tabs */}
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {galleryCategories.map((category) => {
              const isActive = active === category;
              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActive(category)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-200 bg-background-100 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                  }`}
                >
                  {category}
                </button>
              );
            })}
          </div>

          {/* Grid */}
          <div className="mt-9 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {visibleItems.map((item, index) => (
              <button
                key={item.image}
                type="button"
                onClick={() => setLightbox(index)}
                className="group cursor-pointer overflow-hidden rounded-lg border border-background-200 bg-background-50 text-left"
              >
                <span className="block aspect-[4/3] w-full overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    title={`${item.title} - Lahari Packers and Movers Bangalore`}
                    className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                </span>
                <span className="flex items-center justify-between gap-3 p-4">
                  <span>
                    <span className="block font-heading text-sm font-semibold text-foreground-950">{item.title}</span>
                    <span className="mt-0.5 block text-xs text-foreground-500">{item.category}</span>
                  </span>
                  <i className="ri-fullscreen-line text-lg text-primary-500" aria-hidden="true" />
                </span>
              </button>
            ))}
          </div>

          {visibleItems.length === 0 && (
            <p className="mt-10 text-center text-sm text-foreground-500">
              No photos in this category yet. Please check another album.
            </p>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox !== null && visibleItems[lightbox] && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/90 p-4"
          role="dialog"
          aria-modal="true"
          onClick={closeLightbox}
        >
          <button
            type="button"
            aria-label="Close gallery"
            onClick={closeLightbox}
            className="absolute right-4 top-4 flex h-11 w-11 cursor-pointer items-center justify-center rounded-md bg-white/10 text-background-50 hover:bg-white/20 md:right-6 md:top-6"
          >
            <i className="ri-close-line text-2xl" aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label="Previous image"
            onClick={(event) => {
              event.stopPropagation();
              step(-1);
            }}
            className="absolute left-3 flex h-11 w-11 cursor-pointer items-center justify-center rounded-md bg-white/10 text-background-50 hover:bg-white/20 md:left-6"
          >
            <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label="Next image"
            onClick={(event) => {
              event.stopPropagation();
              step(1);
            }}
            className="absolute right-3 flex h-11 w-11 cursor-pointer items-center justify-center rounded-md bg-white/10 text-background-50 hover:bg-white/20 md:right-6"
          >
            <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true" />
          </button>

          <figure className="max-h-full w-full max-w-4xl" onClick={(event) => event.stopPropagation()}>
            <img
              key={lightbox}
              src={visibleItems[lightbox].image}
              alt={visibleItems[lightbox].title}
              title={visibleItems[lightbox].title}
              className="max-h-[70vh] w-full rounded-lg object-contain"
            />
            <figcaption className="mt-4 text-center">
              <span className="block font-heading text-base font-semibold text-background-50">
                {visibleItems[lightbox].title}
              </span>
              <span className="mt-1 block text-sm text-background-300">{visibleItems[lightbox].category}</span>
            </figcaption>
          </figure>
        </div>
      )}

      <CtaBanner
        title="Want your move to look this organised?"
        description="Book Lahari Packers and Movers for your next shift in Bangalore and get the same care our gallery shows."
      />
    </>
  );
}